import { useEffect } from 'react'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { formatColumnValue } from '@/shared/form-utils'
import { getSafely } from '@/shared/safely'
import { useUpdateFormDefaultValue } from '@/components/__project__/hooks/useUpdateFormDefaultValue'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { parseBooleanFromValue } from '../../../utils'
import { CompSwitchProps } from './comp'

export interface CompSwitchWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: CompSwitchProps) => React.ReactNode
}

/**
 * CompSwitchWrapperCore
 */
export function CompSwitchWrapperCore({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex },
  form,
  children,
}: CompSwitchWrapperProps) {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { dataSourceTable, formState, setFormState, setRequiredFieldStatus } = form

  // ========================= data =========================
  const columnId = settings[ComponentSettingKey.SwitchDataValue] as string
  const column = dataSourceTable?.columnsMap?.get(columnId)

  const defaultValue = useParseVariableSelect(
    settings[ComponentSettingKey.SwitchDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const defaultValueBoolean = parseBooleanFromValue(defaultValue)

  const isEditable = getSafely(settings, [ComponentSettingKey.SwitchDataDefaultValueEditable], true)

  const labelData = settings[ComponentSettingKey.SwitchDataLabel]
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)

  // ========================= design =========================

  // ========================= behavior =========================
  const required = settings[ComponentSettingKey.SwitchBehaviorRequired] as boolean

  // ========================= form state =========================
  const selected =
    columnId && typeof formState[columnId] !== 'undefined' ? Boolean(formState[columnId]) : defaultValueBoolean

  const handleChange = () => {
    if (!columnId) return

    setFormState?.((prev) => ({
      ...prev,
      [columnId]: !selected,
    }))
  }

  useUpdateFormDefaultValue({
    defaultValue: formatColumnValue(defaultValue, column),
    value: formState[columnId],
    onUpdate: (defValue) => {
      const newValue = formatColumnValue(defValue, column)
      setFormState((prev) => ({ ...prev, [columnId]: newValue }))
    },
  })

  const switchProps: CompSwitchProps = {
    id: component._id,
    required,
    label,
    checked: selected,
    onCheckedChange: handleChange,
    disabled: !isEditable,
  }

  // ========================= required validation =========================
  useEffect(() => {
    if (required) {
      setRequiredFieldStatus(columnId, !selected)
    }
    return () => {
      if (required) {
        setRequiredFieldStatus(columnId, false)
      }
    }
  }, [required, selected, columnId, setRequiredFieldStatus])

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(switchProps)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(switchProps)
}
